import type { Action } from './action'
import type * as grant from './grant'
import type { ResourceType } from './resource-type'
import type { Urn } from './urn'

/** The evaluator's verdict on one (action, resource) request */
export type Decision =
  | Decision.Allow
  | Decision.ExplicitDeny
  | Decision.ImplicitDeny
export namespace Decision {
  /** An allow matched and no deny did */
  export interface Allow {
    readonly reason: 'allow'
    readonly effect: 'allow'
    readonly decidedBy: DecidedBy
  }
  /** A deny matched; it wins over any allow */
  export interface ExplicitDeny {
    readonly reason: 'explicit-deny'
    readonly effect: 'deny'
    readonly decidedBy: DecidedBy
  }
  /** Nothing matched; the default */
  export interface ImplicitDeny {
    readonly reason: 'implicit-deny'
    readonly effect: 'deny'
  }

  /** The only {@link ImplicitDeny} there is — it carries no data */
  export const IMPLICIT_DENY: ImplicitDeny = {
    effect: 'deny',
    reason: 'implicit-deny',
  }

  export function allow(decidedBy: DecidedBy): Allow {
    return { decidedBy, effect: 'allow', reason: 'allow' }
  }

  export function explicitDeny(decidedBy: DecidedBy): ExplicitDeny {
    return { decidedBy, effect: 'deny', reason: 'explicit-deny' }
  }

  export type Reason = Decision['reason']

  /** The deciding statement */
  export interface DecidedBy {
    readonly sid?: string
    readonly source: grant.Ref
  }
}

/**
 * A {@link Decision} for one action on one resource, as returned by
 * `simulate`. A hint, never a security boundary: every mutation is authorized
 * server-side regardless.
 */
export type SimulateResult = Decision & SimulateCheck

/**
 * One question for `simulate`: may the principal perform this action on this
 * resource — or on some resource, of a type and within a scope as far as
 * they are given?
 */
export type SimulateCheck = SimulateCheck.One | SimulateCheck.Some
export namespace SimulateCheck {
  /** Checks accepted per call */
  export const MAX_PER_CALL = 500

  /** On one concrete resource */
  export interface One {
    readonly action: Action
    readonly resource: Urn
  }

  /**
   * On some resource — for a control tied to no one resource. Neither field:
   * on anything at all
   */
  export interface Some {
    readonly action: Action
    /** Only resources of this type */
    readonly type?: ResourceType
    /** Only resources beneath this one */
    readonly within?: Urn
  }

  export function isOne(check: SimulateCheck): check is One {
    return 'resource' in check
  }
}
